import { createSelector } from '@reduxjs/toolkit';
import { useEffect, useRef } from 'preact/hooks';

import type { TemplateFunction, TemplateResult } from 'Components/types';
import { vehicleDiscardingRequested } from 'Core/actions/fitmentSearch/index';
import { resetRequest, retryRequest } from 'Core/actions/request';
import {
  createFitmentSearchResponseSelectionSelector,
  vehicleWasNotExcludedSelector,
} from 'Core/selectors/fitmentSearch/index';
import {
  nonVehicleSpecificMessageSelector,
  searchOriginalQuerySelector,
  searchQuerySelector,
  searchRequestSelectionSelector,
  searchResponseErrorSelector,
  searchResponseExtraSelector,
  searchResponseMessagesSelector,
  searchResponseStateSelector,
  totalHitsSelector,
} from 'Core/selectors/search';
import { type ReduxStateType, useDispatch, useSelector } from 'Core/store';
import uiConfig from 'Models/uiConfig/uiConfig';
import { getUriFromRequest, keysSelector } from 'Modules/converter/index';
import { Message as MessageType, type SearchErrorInfo } from 'Modules/serverApi/types';

interface Props {
  template: TemplateFunction<MessageTemplateParams>;
  requestAction?: string;
}

type MessageErrorDetails = Pick<SearchErrorInfo, 'instance' | 'status' | 'timestamp' | 'userId'>;

type MessageStateTemplateParams = {
  template: string;
  error?: MessageErrorDetails;
  originalQuery?: string;
  query?: string;
  totalHits?: number;
};

export interface MessageTemplateParams extends MessageStateTemplateParams {
  aggressiveWheels: boolean;
  clearFiters: () => void;
  demoPlanActive: boolean;
  usageLimitExceeded: () => { providerName?: string; contactEmail?: string } | null;
  discardVehicle: () => void;
  hasMessage: (message: MessageType) => boolean;
  incompatibleParts: boolean;
  isFacetsSelected: boolean;
  isSearchedByVehicle: boolean;
  isVehicleSelected: boolean;
  nonVehicleSpecificResults: boolean;
  pageType: string;
  retry: () => void;
  searchByVehicleUrl?: URL;
  startOver: () => void;
  trialExpired: boolean;
  universalFitResults: boolean;
  vehicleString?: string;
  vinVehicleNotFound: boolean;
  vinVehicleNotFull: boolean;
}

export default function Message({ template, requestAction = undefined }: Props): TemplateResult {
  const dispatch = useDispatch();
  const clearTimeoutRef = useRef<(() => void) | null>(null);

  const retry = () => dispatch(retryRequest(requestAction ? { type: requestAction } : undefined));
  const startOver = (discardVehicle: boolean = false) =>
    dispatch(resetRequest({ discardVehicle, allowSearchSameAsUri: true }));
  const discardVehicle = () => dispatch(vehicleDiscardingRequested({ mayDiscardValue: false }));

  const responseState = useSelector(searchResponseStateSelector);

  useEffect(() => {
    if (clearTimeoutRef.current) {
      clearTimeoutRef.current();
    }
    const timeout = uiConfig.noResultsRedirectTimeout;
    if (responseState === 'nothing' && timeout) {
      const prevHref = window.location.href;
      const timeoutId = setTimeout(() => {
        if (window.location.href === prevHref) {
          startOver();
        }
        clearTimeoutRef.current = null;
      }, timeout);
      clearTimeoutRef.current = () => clearTimeout(timeoutId);
    } else {
      clearTimeoutRef.current = null;
    }
  });

  const isFacetsSelected = !!useSelector(searchRequestSelectionSelector).length;
  const nonVehicleSpecificResults = useSelector(nonVehicleSpecificMessageSelector);
  const isSearchedByVehicle = useSelector(vehicleWasNotExcludedSelector);

  const messages = useSelector(searchResponseMessagesSelector);
  const responseExtra = useSelector(searchResponseExtraSelector);

  const vehicleSelection = useSelector(createFitmentSearchResponseSelectionSelector(null));

  const isVehicleSelected = vehicleSelection?.length > 0;

  const vehicleString = vehicleSelection?.map((v) => v.value).join(' ');
  const searchByVehicleUrl = vehicleSelection?.length
    ? getUriFromRequest({ selection: vehicleSelection }, { goToSearchPage: true })
    : undefined;

  return template.call({
    retry,
    startOver: () => startOver(true),
    clearFiters: () => startOver(false),
    discardVehicle,
    isFacetsSelected,
    hasMessage: (message: MessageType) => messages.includes(message),
    pageType: uiConfig.pageType,
    isSearchedByVehicle,
    isVehicleSelected,
    searchByVehicleUrl,
    vehicleString,
    demoPlanActive: messages.includes(MessageType.DemoPlanActive),
    trialExpired: messages.includes(MessageType.TrialExpired),
    aggressiveWheels: messages.includes(MessageType.AggressiveWheels),
    incompatibleParts: messages.includes(MessageType.IncompatibleParts) && isSearchedByVehicle,
    nonVehicleSpecificResults: nonVehicleSpecificResults && isSearchedByVehicle,
    universalFitResults: messages.includes(MessageType.UniversalFitResults),
    usageLimitExceeded: () =>
      messages.includes(MessageType.UsageLimitExceeded)
        ? {
            providerName: responseExtra?.providerName as string | undefined,
            contactEmail: responseExtra?.contactEmail as string | undefined,
          }
        : null,
    vinVehicleNotFound: messages.includes(MessageType.VinVehicleNotFound),
    vinVehicleNotFull: messages.includes(MessageType.VinVehicleNotFull),
    ...useMessageParams(responseState),
  });
}

const emptyState: MessageStateTemplateParams = { template: '' };
const errorKeysSelector: (obj: MessageErrorDetails) => MessageErrorDetails = keysSelector(
  'status',
  'userId',
  'timestamp',
  'instance',
);
const errorParamsSelector = createSelector(searchResponseErrorSelector, (error) => ({
  error: errorKeysSelector(error ?? {}),
})) as (state: ReduxStateType) => Partial<MessageStateTemplateParams>;
const queryWithTotalHitsParamSelector = createSelector(
  searchQuerySelector,
  totalHitsSelector,
  (query, totalHits) => ({
    query,
    totalHits,
  }),
) as (state: ReduxStateType) => Partial<MessageStateTemplateParams>;
const responseStateToSelectorMap: Record<
  string,
  (state: ReduxStateType) => Partial<MessageStateTemplateParams>
> = {
  'badRequest': errorParamsSelector,
  'corrected': createSelector(searchQuerySelector, searchOriginalQuerySelector, (query, originalQuery) => ({
    query,
    originalQuery,
  })) as (state: ReduxStateType) => Partial<MessageStateTemplateParams>,
  'nothing': queryWithTotalHitsParamSelector,
  'partial': createSelector(searchQuerySelector, searchOriginalQuerySelector, (query, originalQuery) => ({
    query,
    originalQuery,
  })) as (state: ReduxStateType) => Partial<MessageStateTemplateParams>,
  'unknown': errorParamsSelector,
  'timeout': errorParamsSelector,
  'cancelled': errorParamsSelector,
  'networkError': errorParamsSelector,
  'normal': queryWithTotalHitsParamSelector,
  '': () => emptyState, // initial state
};

function useMessageParams(responseState: string): MessageStateTemplateParams {
  const key = responseState as keyof typeof responseStateToSelectorMap;
  const selector = responseStateToSelectorMap[key] ?? (() => emptyState);
  const params = useSelector(selector);
  return { template: responseState, ...params };
}
